import { useEffect, useState } from 'react'
import core from './locales/core'
import common from './locales/common'
import shell from './locales/shell'
import home from './locales/home'
import book from './locales/book'
import lab from './locales/lab'
import clinical from './locales/clinical'
import desk from './locales/desk'
import money from './locales/money'
import admin from './locales/admin'
import stock from './locales/stock'
import access from './locales/access'
import reports from './locales/reports'
import ledger from './locales/ledger'
import modules from './locales/modules'
import qbill from './locales/qbill'

/* The phone app's language.
 *
 * Every mobile screen - the patient's and the staff's - and the tickets sent
 * on WhatsApp can be read in English, Hindi, Marathi or Odia. Printed
 * prescriptions, receipts and invoices stay in English: they are clinical and
 * legal documents that pharmacists and insurers read. The desktop screens are
 * English too.
 *
 * Names, codes, dates, amounts and drug names are never translated - they are
 * data. Messages the server sends back (errors) arrive in English.
 *
 * Each screen area keeps its messages in its own module under ./locales,
 * with every language side by side; see ./locales/define.ts. */

export type Lang = 'en' | 'hi' | 'mr' | 'or'

export const LANGS: { code: Lang; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'or', label: 'ଓଡ଼ିଆ' },
]

const KEY = 'clinic.m.lang'
const EVENT = 'clinic-lang-change'

const MODULES = [core, common, shell, home, book, lab, clinical, desk, money, admin, stock, access, reports, ledger, modules, qbill] as const

type KeysOf<T> = T extends { en: infer E } ? keyof E & string : never
export type MsgKey = KeysOf<(typeof MODULES)[number]>

function merge(lang: Lang): Record<MsgKey, string> {
  return Object.assign({}, ...MODULES.map((m) => m[lang])) as Record<MsgKey, string>
}

const en = merge('en')
const DICT: Record<Lang, Record<MsgKey, string>> = { en, hi: merge('hi'), mr: merge('mr'), or: merge('or') }

// Areas are prefixed ("book.", "lab.") so two modules never claim one key;
// if they ever do, the later one silently wins, so say so while developing.
if (import.meta.env.DEV) {
  const seen = new Set<string>()
  for (const m of MODULES) {
    for (const k of Object.keys(m.en)) {
      if (seen.has(k)) console.warn(`i18n: "${k}" is defined in more than one locale module`)
      seen.add(k)
    }
  }
}

export function getLang(): Lang {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'hi' || v === 'mr' || v === 'or' ? v : 'en'
  } catch {
    return 'en'
  }
}

/** Tells the browser (screen readers, fonts, hyphenation) what it is reading. */
function markDocument(lang: Lang): void {
  try { document.documentElement.lang = lang } catch { /* no document, e.g. in tests */ }
}
markDocument(getLang())

export function setLang(lang: Lang): void {
  try { localStorage.setItem(KEY, lang) } catch { /* the choice just will not persist */ }
  markDocument(lang)
  window.dispatchEvent(new Event(EVENT))
}

/** Translate outside React, e.g. while building a WhatsApp message. */
export function translate(lang: Lang, key: MsgKey, vars?: Record<string, string | number>): string {
  let s = DICT[lang][key] ?? en[key] ?? key
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v))
  return s
}

/** Translate in the language currently chosen, outside React. */
export function tr(key: MsgKey, vars?: Record<string, string | number>): string {
  return translate(getLang(), key, vars)
}

/** The chosen language and a translator; re-renders when the choice changes. */
export function useT() {
  const [lang, setState] = useState<Lang>(getLang)
  useEffect(() => {
    const on = () => setState(getLang())
    window.addEventListener(EVENT, on)
    window.addEventListener('storage', on)
    return () => { window.removeEventListener(EVENT, on); window.removeEventListener('storage', on) }
  }, [])
  return {
    lang,
    t: (key: MsgKey, vars?: Record<string, string | number>) => translate(lang, key, vars),
  }
}
